import type { TicketAssignmentResult } from '@easyhelp/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApiError, apiRequest } from './client';
import { teamQueryKey } from './team';
import { ticketQueryKey, ticketsQueryKey } from './tickets';

/**
 * Assegnazione ed escalation (story #4, DEC-004): responsabile e collaboratori di un ticket. Ogni
 * modifica aggiorna subito il dettaglio con la risposta e invalida coda e dettagli (`['tickets']`)
 * e il carico del team (`['team', 'members']`).
 */
function useAssignmentMutation<TVariables>(
  code: string,
  request: (variables: TVariables) => Promise<TicketAssignmentResult>
) {
  const queryClient = useQueryClient();
  return useMutation<TicketAssignmentResult, ApiError, TVariables>({
    mutationFn: request,
    onSuccess: async (result) => {
      queryClient.setQueryData(ticketQueryKey(code), result.ticket);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ticketsQueryKey }),
        queryClient.invalidateQueries({ queryKey: teamQueryKey }),
      ]);
    },
  });
}

const ticketPath = (code: string) => `/tickets/${encodeURIComponent(code)}`;

/** `PUT /tickets/:code/assignee`: assegna o cambia il responsabile. */
export function useSetTicketAssignee(code: string) {
  return useAssignmentMutation<string>(code, (memberId) =>
    apiRequest<TicketAssignmentResult>(`${ticketPath(code)}/assignee`, {
      method: 'PUT',
      body: JSON.stringify({ memberId }),
    })
  );
}

/** `POST /tickets/:code/collaborators`: aggiunge un collaboratore (il primo assegnato diventa responsabile). */
export function useAddTicketCollaborator(code: string) {
  return useAssignmentMutation<string>(code, (memberId) =>
    apiRequest<TicketAssignmentResult>(`${ticketPath(code)}/collaborators`, {
      method: 'POST',
      body: JSON.stringify({ memberId }),
    })
  );
}

/** `DELETE /tickets/:code/collaborators/:memberId`: toglie un collaboratore. */
export function useRemoveTicketCollaborator(code: string) {
  return useAssignmentMutation<string>(code, (memberId) =>
    apiRequest<TicketAssignmentResult>(`${ticketPath(code)}/collaborators/${encodeURIComponent(memberId)}`, {
      method: 'DELETE',
    })
  );
}
